import { useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { OutingCard } from '@/components/outings/OutingCard';
import { useFavorites } from '@/components/favorites/FavoritesProvider';
import { useSavedFeed } from '@/features/account/useSavedFeed';
import type { SavedFeedItem } from '@/features/account/useSavedFeed';
import { useCoverImageUrl } from '@/hooks/useMediaAssets';
import { formatOutingDateLabel, computeDistanceLabel } from '@/lib/outings';
import { track } from '@/lib/analytics';

export function SavedFeedSection() {
  const { data: items = [] } = useSavedFeed();
  const { isFavorited, toggleFavorite } = useFavorites();

  useEffect(() => {
    if (items.length === 0) return;
    track('saved_feed.impression', {
      items_count: items.length,
      event_ids: items.map((item) => item.id),
    });
  }, [items]);

  if (items.length === 0) return null;

  return (
    <section className="rounded-2xl border border-border/60 bg-card/30 p-4 sm:p-5">
      <h2 className="text-base font-semibold text-foreground mb-4">
        Nytt från sparat
      </h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {items.map((item) => {
          const badgeLabel = item.isSoon ? 'Snart' : item.isNew ? 'Nytt' : null;

          return (
            <SavedFeedActivityCard
              key={item.id}
              item={item}
              badgeLabel={badgeLabel}
              isFavorited={isFavorited}
              toggleFavorite={toggleFavorite}
            />
          );
        })}
      </div>
    </section>
  );
}

function SavedFeedActivityCard({
  item,
  badgeLabel,
  isFavorited,
  toggleFavorite,
}: {
  item: SavedFeedItem;
  badgeLabel: 'Snart' | 'Nytt' | null;
  isFavorited: ReturnType<typeof useFavorites>['isFavorited'];
  toggleFavorite: ReturnType<typeof useFavorites>['toggleFavorite'];
}) {
  const coverImageUrl = useCoverImageUrl('event', item.id, 640);

  return (
    <div
      className="relative"
      onClickCapture={(event) => {
        const target = event.target as HTMLElement;
        const link = target.closest('a[href]') as HTMLAnchorElement | null;
        if (!link) return;

        track('saved_feed.click', {
          event_id: item.id,
          badge: badgeLabel,
        });
      }}
    >
      {badgeLabel ? (
        <Badge
          variant="secondary"
          className="absolute left-3 top-3 z-10 pointer-events-none"
        >
          {badgeLabel}
        </Badge>
      ) : null}

      <OutingCard
        id={item.id}
        title={item.title}
        coverImageUrl={coverImageUrl}
        dateLabel={formatOutingDateLabel(item.startDate, item.endDate)}
        distanceLabel={computeDistanceLabel(null, null, item.lat, item.lng)}
        badge={null}
        saved={isFavorited('activity', item.id)}
        onToggleSave={() =>
          toggleFavorite({
            kind: 'activity',
            entityId: item.id,
            slug: item.id,
            title: item.title,
            imagePath: coverImageUrl ?? undefined,
          })
        }
        startDate={item.startDate}
      />
    </div>
  );
}